// config/api.ts

interface ApiConfig {
  baseURL: string;
  endpoints: {
    [key: string]: string;
  };
}

interface EnvironmentConfig {
  development: ApiConfig;
  production: ApiConfig;
}

const config: EnvironmentConfig = {
  development: {
    baseURL: "http://localhost:8000",
    endpoints: {
      login: "/users/api/token/",
      enhancedLogin: "/users/auth/login/",
      complete2faLogin: "/users/auth/2fa/complete/",
      verifySession2fa: "/users/auth/2fa/verify-session/",
      signup: "/users/rest-auth/registration/",
      fetchEmployeeDeductions: "/employees/deductions/",
      fetchEmployeeEarnings: "/employees/get_ee_earnings/",
      fetchAvailableRateTypes: "/employees/get_available_rate_types/",
      ptoPolicy: "/employees/pto/policies/:id/",
      updatePtoPolicy: "/employees/pto/policies/update/:id/",
      validatePtoPolicy: "/employees/pto/policies/validate/:id/",
      removeCustomRate: "/employees/remove_custom_rate/",
      addCustomRate: "/employees/add_custom_rate/",
      timeOff: "/employees/pto/policies/",
      fetchEmployerFilings: "/employees/filings/employer/",
      fetchEmployerFilingDocument: "/employees/filings/filing_document/:id/",
      refreshEmployerFiling: "/employees/filings/employer/:id/refresh/",
      submitFilingManually: "/employees/filings/submit-manually/",
      submitFilingTil: "/employees/filings/submit-til/",
      getTransmissionStatus: "/employees/filings/transmission/:id/",
      getFilingTransmissions: "/employees/filings/:id/transmissions/",
      fetchAllUsers: "/employees/superuser/all_users/",
      fetchImports: "/employees/imports/:id/",
      createP60Import: "/employees/imports/p60/:id/",
      updateP60Import: "/employees/imports/p60/revise/:employeeId/:importId/",
      handleImportProviderToggle:
        "employees/:employeeId/imports/:latestImportId/revise/",
      fetchPayrunEmployees: "/employees/payruns/:id/",
      fetchPayrunEarnings: "/employees/earnings/:id/",
      fetchPayrunDeductions: "/employees/deductions/:id/",
      createEarningOverride: "/employees/create_earning_override/",
      removeEarningOverride: "/employees/remove_earning_override/",
      createDeductionOverride: "/employees/create_deduction_override/",
      removeDeductionOverride: "/employees/remove_deduction_override/",
      refreshPayrun: "/employees/payruns/:id/refresh/",
      fetchPtoSettings: "/employees/pto/settings/",
      updatePtoSettings: "/employees/pto/settings/create/",
      fetchLeavePolicies: "/employees/pto/policies/",
      fetchLeaves: "/employees/pto/get_leaves/",
      getLeaveTypes: "/employees/pto/get_leaves/",
      fetchPensionsOverview: "/employees/benefits/overview/",
      fetchEnrollmentDetails: "/employees/benefits/:id/detail/",
      createEnrollment: "/employees/benefits/enrollment/create/:id/",
      submitSelections: "/employees/benefits/enrollment/:id/selections/",
      getEmployees: "/employees/get_employee_personal_information/",
      createEmployee: "/employees/create_employee_personal_information/",
      getPayruns: "/employees/payruns/",
      updateEmployee: "/employees/:id/update/",
      getEmployeeDetails:
        "/employees/get_employee_demographic_information/:id/",
      createPayrun: "/employees/create_payruns/",
      getPayrunEmployees: "/employees/payruns/:id/",
      approvePayrun: "/employees/payruns/:id/state/",
      unapprovePayrun: "/employees/payruns/:id/unapprove/",
      downloadPaystub: "/employees/payruns/:id/paystub/",
      fetchEmployer: "/employees/get_uk_employer/",
      createEmployer: "/employees/create_uk_employer/",
      updateDemographicInfo:
        "/employees/update_employee_personal_information/:employeeId/",
      createDemographicInfo: "/employees/create_employee_personal_information/",
      getJobTimeline: "/employees/get_job_timeline/:employeeId/",
      updateJobTimeline: "/employees/update_job_timeline/:employeeId/",
      // Job Quick Actions endpoints
      getJobQuickActionsTimeline: "/employees/:employeeId/job/timeline/",
      getJobTimelineSummary: "/employees/:employeeId/job/timeline/summary/",
      validateSetupStep: "/employees/:employeeId/job/setup/validate/",
      commitSetupWizard: "/employees/:employeeId/job/setup/commit/",
      // Post-hire actions (Phase 3)
      createPayChange: "/employees/:employeeId/job/pay-change/",
      createHoursChange: "/employees/:employeeId/job/hours-change/",
      getNextPaydayInfo: "/employees/:employeeId/job/next-payday-info/",
      // Guardrails for basis switch
      getBasisGuardrails: "/employees/:employeeId/job/basis-guardrails/",
      getEmployeeStartDateInfo: "/employees/:employeeId/job/start-date-info/",
      // Phase 4 actions
      createBasisSwitch: "/employees/:employeeId/job/basis-switch/",
      createEmploymentTermination: "/employees/:employeeId/job/end-employment/",
      logTimeOff: "/employees/pto/leaves/",
      updateTaxInfo: "/employees/:employeeId/tax/",
      getParentInfo: "/employees/parents/info/",
      createUpdateParentInfo: "/employees/parents/info/create-update/",
      validateParentInfo: "/employees/parents/info/validate/",
      checkPayrunReadiness: "/employees/payruns/:id/readiness/",
      // Nanny submission endpoints
      getSubmission: "/employees/nanny/submissions/:id/",
      submitSubmission: "/employees/nanny/submissions/:id/submit/",
      getEmployeeSubmissions: "/employees/nanny/employee-submissions/",
      approveSubmission: "/employees/nanny/submissions/:submissionId/approve/",
      rejectSubmission: "/employees/nanny/submissions/:submissionId/reject/",
      reopenSubmission: "/employees/nanny/submissions/:id/reopen/",
      reopenSubmissionAdmin: "/employees/nanny/submissions/:id/reopen-admin/",

      // Hours worked endpoints
      createHours: "/employees/nanny/submissions/:submissionId/hours/",
      deleteHours: "/employees/nanny/submissions/:submissionId/hours/:id/",

      // Expenses endpoints
      createExpense: "/employees/nanny/submissions/:submissionId/expenses/",
      deleteExpense: "/employees/nanny/submissions/:submissionId/expenses/:id/",
      getExpenseUploadUrl:
        "/employees/nanny/submissions/:submissionId/expenses/upload-url/",
      getReceiptDownloadUrl: "/employees/nanny/expenses/download-url/",

      // Time off request endpoints
      createTimeOff: "/employees/nanny/submissions/:submissionId/timeoff/",
      deleteTimeOff: "/employees/nanny/submissions/:submissionId/timeoff/:id/",
      // Add other endpoints here
      getNannyInvites: "/employees/nanny/invites/",
      sendNannyInvites: "/employees/nanny/invites/send/:employeeId/",
      validateNannyInvite: "/employees/nanny/invite/validate/:code/",
      registerNanny: "/employees/nanny/register/",
      getUserDetails: "/users/me/",
      getEmployeePayslips: "/employees/payslips/:employeeId/",
      getEmployerChecklist: "/employees/employers/setup/checklist/",
      getOnboardingIncompleteCount:
        "/employees/employers/setup/incomplete-count/",

      // dashboard views
      getDashboardView: "/employees/dashboard_view/",
      executeAdminCode: "/employees/admin/execute-code/",
      save_admin_script: "/employees/admin/scripts/save/",
      get_admin_scripts: "/employees/admin/scripts/",
      getBillingPlans: "/employees/billing/plans/",
      validateDiscountCode: "/employees/billing/discount/validate/",
      createSubscription: "/employees/billing/subscriptions/create/",
      cancelSubscription: "/employees/billing/subscriptions/cancel/",
      getSubscription: "/employees/billing/subscriptions/:id/",
      createSetupIntent: "/employees/billing/setup-intent/",
      updatePaymentMethod: "/employees/billing/payment-method/update/",
      createAffiliate: "/employees/billing/affiliate/create/",
      getAffiliateStats: "/employees/billing/affiliate/stats/",
      getAffiliateCommissions: "/employees/billing/affiliate/commissions/",
      createPaymentIntent: "/employees/billing/paymentIntent/",
      attachPaymentMethod: "/employees/billing/paymentIntent/attach/",
      // Seat subscription endpoints
      getSeatSummary: "/employees/billing/seats/summary",
      createSeatCheckout: "/employees/billing/seats/checkout",
      updateSeats: "/employees/billing/seats/update",
      generateAgreement: "/employees/agreements/generate/",
      downloadAgreement: "/employees/agreements/:agreement_id/download/",
      getAgreementDownloadUrl: "/employees/agreements/download-url/",
      listEmployeeAgreements: "/employees/agreements/employee/:employee_id/",
      listEmployerAgreements: "/employees/agreements/",
      requestPasswordReset: "/users/auth/password-reset/request/",
      confirmPasswordReset: "/users/auth/password-reset/confirm/",
      validateResetToken: "/users/auth/password-reset/validate/:token/",
      updateSubscriptionImplementationStatus:
        "/employees/billing/subscriptions/implementation/",
      // Operations Dashboard Endpoints
      getEmployerList: "/operations/api/employers",
      getEmployerDetail: "/operations/api/employers/:employerId",
      getEmployerIssues: "/operations/api/employer-issues",
      getActiveIssues: "/operations/api/active-issues",
      getIssuesByCategory: "/operations/api/issues-by-category",
      getIssueDetail: "/operations/api/issues/:issueId",
      overrideIssue: "/operations/api/issues/:issueId/override",
      getAffiliatePartners: "/operations/api/affiliate-partners",
      getImplementationEmployers: "/operations/api/employers/implementation/",
      getFeatures: "/operations/api/features",
      toggleFeature: "/operations/api/features/:id/toggle",
      getFeatureFlags: "/api/feature-flags/",
      toggleFeatureFlag: "/api/feature-flags/:key/",
      createFeatureFlag: "/api/feature-flags/",
      updateFeatureFlag: "/api/feature-flags/:key/",
      getUserFeatureFlags: "/api/user-feature-flags/",
      createUserFeatureFlag: "/api/user-feature-flags/",
      deleteUserFeatureFlag: "/api/user-feature-flags/",
      getPublicFeatureFlags: "/api/public-feature-flags/",
      // Contractor entities endpoints
      getContractorEntities: "/operations/api/contractors/entities/",
      updateContractorEntityStatus:
        "/operations/api/contractors/entities/:entityId/status/",
      // Termination endpoints
      getTermination: "/employees/api/termination/:employee_id",
      createTermination: "/employees/api/termination/create",
      updateTermination: "/employees/api/termination/:termination_id/update",
      submitTermination: "/employees/api/termination/:termination_id/submit",
      approveTermination:
        "/operations/api/terminations/:termination_id/approve/",
      listPendingTerminations: "/operations/api/pending-terminations/",
      getFinalPayComponents: "/employees/api/termination/final-pay-components",
      // Add working hours endpoints
      getEmployeeSchedule: "/employees/working_hours/:employee_id/schedule/",
      getEmployeeScheduleHistory:
        "/employees/working_hours/:employee_id/schedule/history/",
      updateEmployeeSchedule:
        "/employees/working_hours/:employee_id/schedule/update/",
      requestDemoAccounts: "/employees/api/demo/request/",
      requestContractorDemoAccounts: "/contractor/demo/request/",
      getCountryFromIp: "/contractor/ip-country/",
      getAgreementDetails: "/employees/agreements/:agreement_id/details/",
      updateAgreement: "/employees/agreements/:agreement_id/update/",
      deleteAgreement: "/employees/agreements/:agreement_id/delete/",
      generateLiteAgreement: "/employees/agreements/lite/generate/",

      // Contractor agreement endpoints
      generateContractorAgreement: "/employees/agreements/contractor/generate/",
      downloadContractorAgreement:
        "/contractor/agreements/:agreement_id/download/",
      listContractorAgreements: "/contractor/agreements",

      // Contractor quiz endpoints
      submitContractorQuiz: "/employees/api/contractor-quiz/submit/",
      getContractorQuizResult:
        "/employees/api/contractor-quiz/result/:submission_id/",

      // AI Contract Builder endpoints
      contractBuilderCreate: "/employees/api/contract-builder/create/",
      contractBuilderGet: "/employees/api/contract-builder/:contractId/",
      contractBuilderAIEdit: "/employees/api/contract-builder/ai-edit",
      contractBuilderSave: "/employees/api/contract-builder/:contractId/save/",
      contractBuilderFinalize:
        "/employees/api/contract-builder/:contractId/finalize/",
      contractBuilderMyDrafts: "/employees/api/contract-builder/my-drafts/",
      contractBuilderAccessEditor:
        "/employees/api/contract-builder/access/:token/",
      contractBuilderUpdateVariables:
        "/employees/api/contract-builder/:contractId/update-variables/",

      salaryCalculatorCalculation: "/api/salary-calculator/calculations/",
      salaryCalculatorLangGraphChat: "/api/salary-calculator/langgraph/chat/",
      // New endpoint
      operationsContractors: "/operations/api/contractors/",
      operationsCreateContractor: "/operations/api/contractors/create/",
      saveContractorEntity: "/contractor/entity/save/",
      getContractorEntity: "/contractor/entity/",
      updateContractorEntity: "/contractor/entity/update/",

      // Invoice endpoints
      getInvoices: "/contractor/invoices/",
      getAvailableContracts: "/contractor/invoices/contracts/",
      createInvoice: "/contractor/invoices/create/",
      updateInvoice: "/contractor/invoices/:invoiceId/update/",
      getInvoiceDetails: "/contractor/invoices/:invoiceId/",
      updateInvoiceStatus: "/contractor/invoices/:invoiceId/status/",
      submitInvoice: "/contractor/invoices/:invoiceId/submit/",
      deleteInvoice: "/contractor/invoices/:invoiceId/delete/",
      downloadInvoiceDocument: "/contractor/invoices/:invoiceId/document/",

      // Contractor contract endpoints
      getContractorContracts: "/contractor/agreements/",
      createContractorContract: "/contractor/agreements/create/",
      updateContractorContract: "/contractor/contracts/:contractId/",

      // Client invite endpoints
      sendClientInvite: "/contractor/contracts/:contractId/invite/",
      validateClientInvite: "/contractor/invite/validate/:code/",
      registerClient: "/contractor/register/",

      // Client invoice endpoints
      getClientContracts: "/contractor/client/contracts/",
      approveClientContract:
        "/contractor/client/contracts/:contractId/approve/",
      getClientInvoices: "/contractor/client/invoices/",
      getClientInvoiceDetails: "/contractor/client/invoices/:invoiceId/",
      approveClientInvoice: "/contractor/client/invoices/:invoiceId/approve/",
      rejectClientInvoice: "/contractor/client/invoices/:invoiceId/reject/",
      getClientDashboardData: "/contractor/dashboard/client/",
      // One-time payment endpoints
      createOneTimePayment: "/employees/billing/one-time-payment/create/",
      confirmOneTimePayment: "/employees/billing/one-time-payment/confirm/",
      getEntitySetupPlan: "/employees/billing/entity-setup-plan/",

      // Onboarding override endpoints
      createOnboardingOverride:
        "/employees/admin/employers/:employerId/overrides/",
      removeOnboardingOverride:
        "/employees/admin/employers/:employerId/overrides/:stepType/",
      getEmployerOverrides:
        "/employees/admin/employers/:employerId/overrides/list/",
      getAvailableOverrideStepTypes: "/employees/admin/overrides/step-types/",

      // Contractor expense endpoints
      contractorExpensesCreate: "/contractor/expenses/create/",
      contractorExpensesSubmit: "/contractor/expenses/:id/submit/",
      contractorExpensesList: "/contractor/expenses/",
      contractorExpensesDetails: "/contractor/expenses/:id/",
      contractorExpensesSummary: "/contractor/expenses/summary/",
      contractorExpensesApproveReject:
        "/contractor/expenses/:id/approve-reject/",
      contractorContractExpensesPendingApproval:
        "/contractor/contracts/:id/expenses/review/",
      contractorContractExpensesApproved:
        "/contractor/contracts/:id/expenses/approved/",
      contractorExpensesMarkInvoiced:
        "/contractor/expenses/:id/mark-as-invoiced/",
      contractorExpenseReceiptUploadUrl:
        "/contractor/expenses/receipt/upload-url/",
      contractorExpenseReceiptDownloadUrl:
        "/contractor/expenses/receipt/download-url/",
      contractorContractExpensesSummary:
        "/contractor/contracts/:id/expenses/summary/",

      // Payroll Validator endpoints
      createValidationSession: "/api/payroll-validator/sessions/create/",
      getValidationSession: "/api/payroll-validator/sessions/:sessionId/",
      getValidationSessionStatus: "/api/payroll-validator/sessions/:sessionId/status/",
      getValidationSessionSummary: "/api/payroll-validator/sessions/:sessionId/summary/",
      getValidationUploadUrl: "/api/payroll-validator/upload-url/",
      registerValidationDocument: "/api/payroll-validator/documents/register/",
      startValidation: "/api/payroll-validator/sessions/:sessionId/validate/",
      getValidationIssues: "/api/payroll-validator/sessions/:sessionId/issues/",
      getValidationIssue: "/api/payroll-validator/sessions/:sessionId/issues/:issueId/",
      getSessionEmployees: "/api/payroll-validator/sessions/:sessionId/employees/",
      getEmployeesWithIssues: "/api/payroll-validator/sessions/:sessionId/employees-with-issues/",
      // Demo endpoints (stateless)
      validateBikDemo: "/api/payroll-validator/demo/validate-bik/",
      parseFpsDemo: "/api/payroll-validator/demo/parse-fps/",
      uploadEPSData: "/api/payroll-validator/sessions/:sessionId/eps/upload/",
      getEPSReconciliation: "/api/payroll-validator/sessions/:sessionId/eps/reconciliation/",
      getStatutoryReports: "/api/payroll-validator/sessions/:sessionId/statutory-reports/",
      listSessionExports: "/api/payroll-validator/sessions/:sessionId/exports/",
      downloadEarningsExport: "/api/payroll-validator/sessions/:sessionId/exports/earnings/",
      downloadDeductionsExport: "/api/payroll-validator/sessions/:sessionId/exports/deductions/",
      downloadPayRegisterExport: "/api/payroll-validator/sessions/:sessionId/exports/pay-register/",
      downloadPensionsExport: "/api/payroll-validator/sessions/:sessionId/exports/pensions/",

      // Pension setup endpoints
      savePensionBankAccount: "/employees/benefits/pension-setup/bank-account/",
      recordPensionConsent: "/employees/benefits/pension-setup/consent/",
      validatePensionSetup: "/employees/benefits/pension-setup/validate/",
      validatePensionPostponement:
        "/employees/benefits/pension-setup/validate-postponement/",
      getPensionBankAccount:
        "/employees/benefits/pension-setup/bank-account/get/",
      getPensionConsent: "/employees/benefits/pension-setup/consent/get/",
      getPensionTermsPdf: "/employees/benefits/pension-setup/terms-pdf/",

      // Pension Quick Actions
      pensionSetup: "/employees/benefits/pensions/:employeeId/setup/",
      pensionPostpone: "/employees/benefits/pensions/:employeeId/postpone/",
      pensionCatchUp: "/employees/benefits/pensions/:employeeId/catch-up/",
      pensionChangeEmployerContribution:
        "/employees/benefits/pensions/:employeeId/employer-contribution/",
      pensionEmployeeEnroll:
        "/employees/benefits/pensions/:employeeId/employee/enroll/",
      pensionEmployeeOptOut:
        "/employees/benefits/pensions/:employeeId/employee/opt-out/",
      pensionEmployeeReenroll:
        "/employees/benefits/pensions/:employeeId/employee/re-enroll/",
      pensionEmployeeChangeContribution:
        "/employees/benefits/pensions/:employeeId/employee/contribution/",
      pensionStatus: "/employees/benefits/pensions/:employeeId/status/",
      pensionPostponementStatus:
        "/employees/benefits/pensions/:employeeId/postponement-status/",

      // Two-Factor Authentication endpoints
      getTwoFactorStatus: "/users/2fa/status/",
      initiateTwoFactorSetup: "/users/2fa/setup/initiate/",
      completeTwoFactorSetup: "/users/2fa/setup/complete/",
      verifyTwoFactorCode: "/users/2fa/verify/",
      disableTwoFactor: "/users/2fa/disable/",
      regenerateBackupCodes: "/users/2fa/backup-codes/regenerate/",
      getTwoFactorAuditLog: "/users/2fa/audit-log/",

      // Task Status Monitor endpoints
      taskStatusSummary: "/operations/api/task-status/",
      taskHistory: "/operations/api/task-status/:task_key/history/",
      // Demo Accounts endpoint
      getDemoAccounts: "/operations/api/demo-accounts/",

      // Multi-Country Platform endpoints
      getEnabledCountries: "/contractor/countries/",
      getCountryConfig: "/contractor/country-config/:countryCode/",
      usContractorOnboard: "/contractor/us/onboard/",
      getCountryProfile: "/contractor/profile/country/",
      updateCountryProfile: "/contractor/profile/country/",

      // Stripe OAuth endpoints
      stripeOAuthConnect: "/contractor/stripe/oauth/connect/",
      stripeOAuthCallback: "/contractor/stripe/oauth/callback/",
      stripeDisconnect: "/contractor/stripe/oauth/disconnect/",
      stripeStatus: "/contractor/stripe/oauth/status/",

      // Payment link endpoints for clients
      invoicePaymentLink:
        "/contractor/client/invoices/:invoiceId/payment-link/",

      // EPS endpoints
      generateEpsNoPayment: "/employees/api/filings/eps/generate-no-payment/",
      previewEps: "/employees/api/filings/eps/preview/",
      getEpsPeriods: "/employees/api/filings/eps/periods/",
      getEpsFilings: "/employees/api/filings/eps/filings/",
      validateEpsRequest: "/employees/api/filings/eps/validate/",

      // Customer AI endpoints
      customerAIChat: "/api/customer-ai/chat/",
      customerAIMultiAgentChat: "/api/customer-ai/multi-agent-chat/",
      customerAIHistory: "/api/customer-ai/history/:session_id/",
      customerAISessions: "/api/customer-ai/sessions/",
    },
  },
  production: {
    baseURL: "https://api.hirelo.io",
    endpoints: {
      login: "/users/api/token/",
      enhancedLogin: "/users/auth/login/",
      complete2faLogin: "/users/auth/2fa/complete/",
      verifySession2fa: "/users/auth/2fa/verify-session/",
      signup: "/users/rest-auth/registration/",
      fetchEmployeeDeductions: "/employees/deductions/",
      fetchEmployeeEarnings: "/employees/get_ee_earnings/",
      fetchAvailableRateTypes: "/employees/get_available_rate_types/",
      ptoPolicy: "/employees/pto/policies/:id/",
      updatePtoPolicy: "/employees/pto/policies/update/:id/",
      validatePtoPolicy: "/employees/pto/policies/validate/:id/",
      removeCustomRate: "/employees/remove_custom_rate/",
      addCustomRate: "/employees/add_custom_rate/",
      timeOff: "/employees/pto/policies/",
      fetchEmployerFilings: "/employees/filings/employer/",
      fetchEmployerFilingDocument: "/employees/filings/filing_document/:id/",
      refreshEmployerFiling: "/employees/filings/employer/:id/refresh/",
      submitFilingManually: "/employees/filings/submit-manually/",
      submitFilingTil: "/employees/filings/submit-til/",
      getTransmissionStatus: "/employees/filings/transmission/:id/",
      getFilingTransmissions: "/employees/filings/:id/transmissions/",
      fetchAllUsers: "/employees/superuser/all_users/",
      fetchImports: "/employees/imports/:id/",
      createP60Import: "/employees/imports/p60/:id/",
      updateP60Import: "/employees/imports/p60/revise/:employeeId/:importId/",
      handleImportProviderToggle:
        "employees/:employeeId/imports/:latestImportId/revise/",
      fetchPayrunEmployees: "/employees/payruns/:id/",
      fetchPayrunEarnings: "/employees/earnings/:id/",
      fetchPayrunDeductions: "/employees/deductions/:id/",
      createEarningOverride: "/employees/create_earning_override/",
      removeEarningOverride: "/employees/remove_earning_override/",
      createDeductionOverride: "/employees/create_deduction_override/",
      removeDeductionOverride: "/employees/remove_deduction_override/",
      refreshPayrun: "/employees/payruns/:id/refresh/",
      fetchPtoSettings: "/employees/pto/settings/",
      updatePtoSettings: "/employees/pto/settings/create/",
      fetchLeavePolicies: "/employees/pto/policies/",
      fetchLeaves: "/employees/pto/get_leaves/",
      getLeaveTypes: "/employees/pto/get_leaves/",
      fetchPensionsOverview: "/employees/benefits/overview/",
      fetchEnrollmentDetails: "/employees/benefits/:id/detail/",
      createEnrollment: "/employees/benefits/enrollment/create/:id/",
      submitSelections: "/employees/benefits/enrollment/:id/selections/",
      getEmployees: "/employees/get_employee_personal_information/",
      createEmployee: "/employees/create_employee_personal_information/",
      getPayruns: "/employees/payruns/",
      updateEmployee: "/employees/:id/update/",
      getEmployeeDetails:
        "/employees/get_employee_demographic_information/:id/",
      createPayrun: "/employees/create_payruns/",
      getPayrunEmployees: "/employees/payruns/:id/",
      approvePayrun: "/employees/payruns/:id/state/",
      unapprovePayrun: "/employees/payruns/:id/unapprove/",
      downloadPaystub: "/employees/payruns/:id/paystub/",
      fetchEmployer: "/employees/get_uk_employer/",
      createEmployer: "/employees/create_uk_employer/",
      updateDemographicInfo:
        "/employees/update_employee_personal_information/:employeeId/",
      createDemographicInfo: "/employees/create_employee_personal_information/",
      getJobTimeline: "/employees/get_job_timeline/:employeeId/",
      updateJobTimeline: "/employees/update_job_timeline/:employeeId/",
      // Job Quick Actions endpoints
      getJobQuickActionsTimeline: "/employees/:employeeId/job/timeline/",
      getJobTimelineSummary: "/employees/:employeeId/job/timeline/summary/",
      validateSetupStep: "/employees/:employeeId/job/setup/validate/",
      commitSetupWizard: "/employees/:employeeId/job/setup/commit/",
      // Post-hire actions (Phase 3)
      createPayChange: "/employees/:employeeId/job/pay-change/",
      createHoursChange: "/employees/:employeeId/job/hours-change/",
      getNextPaydayInfo: "/employees/:employeeId/job/next-payday-info/",
      // Guardrails for basis switch
      getBasisGuardrails: "/employees/:employeeId/job/basis-guardrails/",
      getEmployeeStartDateInfo: "/employees/:employeeId/job/start-date-info/",
      // Phase 4 actions
      createBasisSwitch: "/employees/:employeeId/job/basis-switch/",
      createEmploymentTermination: "/employees/:employeeId/job/end-employment/",
      logTimeOff: "/employees/pto/leaves/",
      updateTaxInfo: "/employees/:employeeId/tax/",
      getParentInfo: "/employees/parents/info/",
      createUpdateParentInfo: "/employees/parents/info/create-update/",
      validateParentInfo: "/employees/parents/info/validate/",
      checkPayrunReadiness: "/employees/payruns/:id/readiness/",
      // Nanny submission endpoints
      getSubmission: "/employees/nanny/submissions/:id/",
      submitSubmission: "/employees/nanny/submissions/:id/submit/",
      getEmployeeSubmissions: "/employees/nanny/employee-submissions/",
      approveSubmission: "/employees/nanny/submissions/:submissionId/approve/",
      rejectSubmission: "/employees/nanny/submissions/:submissionId/reject/",
      reopenSubmission: "/employees/nanny/submissions/:id/reopen/",
      reopenSubmissionAdmin: "/employees/nanny/submissions/:id/reopen-admin/",

      // Hours worked endpoints
      createHours: "/employees/nanny/submissions/:submissionId/hours/",
      deleteHours: "/employees/nanny/submissions/:submissionId/hours/:id/",

      // Expenses endpoints
      createExpense: "/employees/nanny/submissions/:submissionId/expenses/",
      deleteExpense: "/employees/nanny/submissions/:submissionId/expenses/:id/",
      getExpenseUploadUrl:
        "/employees/nanny/submissions/:submissionId/expenses/upload-url/",
      getReceiptDownloadUrl: "/employees/nanny/expenses/download-url/",

      // Time off request endpoints
      createTimeOff: "/employees/nanny/submissions/:submissionId/timeoff/",
      deleteTimeOff: "/employees/nanny/submissions/:submissionId/timeoff/:id/",
      // Add other endpoints here
      getNannyInvites: "/employees/nanny/invites/",
      sendNannyInvites: "/employees/nanny/invites/send/:employeeId/",
      validateNannyInvite: "/employees/nanny/invite/validate/:code/",
      registerNanny: "/employees/nanny/register/",
      getUserDetails: "/users/me/",
      getEmployeePayslips: "/employees/payslips/:employeeId/",
      getEmployerChecklist: "/employees/employers/setup/checklist/",
      getOnboardingIncompleteCount:
        "/employees/employers/setup/incomplete-count/",

      // dashboard views
      getDashboardView: "/employees/dashboard_view/",
      executeAdminCode: "/employees/admin/execute-code/",
      save_admin_script: "/employees/admin/scripts/save/",
      get_admin_scripts: "/employees/admin/scripts/",
      getBillingPlans: "/employees/billing/plans/",
      validateDiscountCode: "/employees/billing/discount/validate/",
      createSubscription: "/employees/billing/subscriptions/create/",
      cancelSubscription: "/employees/billing/subscriptions/cancel/",
      getSubscription: "/employees/billing/subscriptions/:id/",
      createSetupIntent: "/employees/billing/setup-intent/",
      updatePaymentMethod: "/employees/billing/payment-method/update/",
      createAffiliate: "/employees/billing/affiliate/create/",
      getAffiliateStats: "/employees/billing/affiliate/stats/",
      getAffiliateCommissions: "/employees/billing/affiliate/commissions/",
      createPaymentIntent: "/employees/billing/paymentIntent/",
      attachPaymentMethod: "/employees/billing/paymentIntent/attach/",
      // Seat subscription endpoints
      getSeatSummary: "/employees/billing/seats/summary",
      createSeatCheckout: "/employees/billing/seats/checkout",
      updateSeats: "/employees/billing/seats/update",
      generateAgreement: "/employees/agreements/generate/",
      downloadAgreement: "/employees/agreements/:agreement_id/download/",
      getAgreementDownloadUrl: "/employees/agreements/download-url/",
      listEmployeeAgreements: "/employees/agreements/employee/:employee_id/",
      listEmployerAgreements: "/employees/agreements/",
      requestPasswordReset: "/users/auth/password-reset/request/",
      confirmPasswordReset: "/users/auth/password-reset/confirm/",
      validateResetToken: "/users/auth/password-reset/validate/:token/",
      updateSubscriptionImplementationStatus:
        "/employees/billing/subscriptions/implementation/",
      // Operations Dashboard Endpoints
      getEmployerList: "/operations/api/employers",
      getEmployerDetail: "/operations/api/employers/:employerId",
      getEmployerIssues: "/operations/api/employer-issues",
      getActiveIssues: "/operations/api/active-issues",
      getIssuesByCategory: "/operations/api/issues-by-category",
      getIssueDetail: "/operations/api/issues/:issueId",
      overrideIssue: "/operations/api/issues/:issueId/override",
      getAffiliatePartners: "/operations/api/affiliate-partners",
      getImplementationEmployers: "/operations/api/employers/implementation/",
      getFeatures: "/operations/api/features",
      toggleFeature: "/operations/api/features/:id/toggle",
      getFeatureFlags: "/api/feature-flags/",
      toggleFeatureFlag: "/api/feature-flags/:key/",
      createFeatureFlag: "/api/feature-flags/",
      updateFeatureFlag: "/api/feature-flags/:key/",
      getUserFeatureFlags: "/api/user-feature-flags/",
      createUserFeatureFlag: "/api/user-feature-flags/",
      deleteUserFeatureFlag: "/api/user-feature-flags/",
      getPublicFeatureFlags: "/api/public-feature-flags/",
      // Contractor entities endpoints
      getContractorEntities: "/operations/api/contractors/entities/",
      updateContractorEntityStatus:
        "/operations/api/contractors/entities/:entityId/status/",
      // Termination endpoints
      getTermination: "/employees/api/termination/:employee_id",
      createTermination: "/employees/api/termination/create",
      updateTermination: "/employees/api/termination/:termination_id/update",
      submitTermination: "/employees/api/termination/:termination_id/submit",
      approveTermination:
        "/operations/api/terminations/:termination_id/approve/",
      listPendingTerminations: "/operations/api/pending-terminations/",
      getFinalPayComponents: "/employees/api/termination/final-pay-components",
      // Add working hours endpoints
      getEmployeeSchedule: "/employees/working_hours/:employee_id/schedule/",
      getEmployeeScheduleHistory:
        "/employees/working_hours/:employee_id/schedule/history/",
      updateEmployeeSchedule:
        "/employees/working_hours/:employee_id/schedule/update/",
      requestDemoAccounts: "/employees/api/demo/request/",
      requestContractorDemoAccounts: "/contractor/demo/request/",
      getCountryFromIp: "/contractor/ip-country/",
      getAgreementDetails: "/employees/agreements/:agreement_id/details/",
      updateAgreement: "/employees/agreements/:agreement_id/update/",
      deleteAgreement: "/employees/agreements/:agreement_id/delete/",
      generateLiteAgreement: "/employees/agreements/lite/generate/",

      // Contractor agreement endpoints
      generateContractorAgreement: "/employees/agreements/contractor/generate/",
      downloadContractorAgreement:
        "/contractor/agreements/:agreement_id/download/",
      listContractorAgreements: "/contractor/agreements",

      // Contractor quiz endpoints
      submitContractorQuiz: "/employees/api/contractor-quiz/submit/",
      getContractorQuizResult:
        "/employees/api/contractor-quiz/result/:submission_id/",

      // AI Contract Builder endpoints
      contractBuilderCreate: "/employees/api/contract-builder/create/",
      contractBuilderGet: "/employees/api/contract-builder/:contractId/",
      contractBuilderAIEdit: "/employees/api/contract-builder/ai-edit",
      contractBuilderSave: "/employees/api/contract-builder/:contractId/save/",
      contractBuilderFinalize:
        "/employees/api/contract-builder/:contractId/finalize/",
      contractBuilderMyDrafts: "/employees/api/contract-builder/my-drafts/",
      contractBuilderAccessEditor:
        "/employees/api/contract-builder/access/:token/",
      contractBuilderUpdateVariables:
        "/employees/api/contract-builder/:contractId/update-variables/",

      salaryCalculatorCalculation: "/api/salary-calculator/calculations/",
      salaryCalculatorLangGraphChat: "/api/salary-calculator/langgraph/chat/",
      // New endpoint
      operationsContractors: "/operations/api/contractors/",
      operationsCreateContractor: "/operations/api/contractors/create/",
      saveContractorEntity: "/contractor/entity/save/",
      getContractorEntity: "/contractor/entity/",
      updateContractorEntity: "/contractor/entity/update/",

      // Invoice endpoints
      getInvoices: "/contractor/invoices/",
      getAvailableContracts: "/contractor/invoices/contracts/",
      createInvoice: "/contractor/invoices/create/",
      updateInvoice: "/contractor/invoices/:invoiceId/update/",
      getInvoiceDetails: "/contractor/invoices/:invoiceId/",
      updateInvoiceStatus: "/contractor/invoices/:invoiceId/status/",
      submitInvoice: "/contractor/invoices/:invoiceId/submit/",
      deleteInvoice: "/contractor/invoices/:invoiceId/delete/",
      downloadInvoiceDocument: "/contractor/invoices/:invoiceId/document/",

      // Contractor contract endpoints
      getContractorContracts: "/contractor/agreements/",
      createContractorContract: "/contractor/agreements/create/",
      updateContractorContract: "/contractor/contracts/:contractId/",

      // Client invite endpoints
      sendClientInvite: "/contractor/contracts/:contractId/invite/",
      validateClientInvite: "/contractor/invite/validate/:code/",
      registerClient: "/contractor/register/",

      // Client invoice endpoints
      getClientContracts: "/contractor/client/contracts/",
      approveClientContract:
        "/contractor/client/contracts/:contractId/approve/",
      getClientInvoices: "/contractor/client/invoices/",
      getClientInvoiceDetails: "/contractor/client/invoices/:invoiceId/",
      approveClientInvoice: "/contractor/client/invoices/:invoiceId/approve/",
      rejectClientInvoice: "/contractor/client/invoices/:invoiceId/reject/",
      getClientDashboardData: "/contractor/dashboard/client/",
      // One-time payment endpoints
      createOneTimePayment: "/employees/billing/one-time-payment/create/",
      confirmOneTimePayment: "/employees/billing/one-time-payment/confirm/",
      getEntitySetupPlan: "/employees/billing/entity-setup-plan/",

      // Onboarding override endpoints
      createOnboardingOverride:
        "/employees/admin/employers/:employerId/overrides/",
      removeOnboardingOverride:
        "/employees/admin/employers/:employerId/overrides/:stepType/",
      getEmployerOverrides:
        "/employees/admin/employers/:employerId/overrides/list/",
      getAvailableOverrideStepTypes: "/employees/admin/overrides/step-types/",

      // Contractor expense endpoints
      contractorExpensesCreate: "/contractor/expenses/create/",
      contractorExpensesSubmit: "/contractor/expenses/:id/submit/",
      contractorExpensesList: "/contractor/expenses/",
      contractorExpensesDetails: "/contractor/expenses/:id/",
      contractorExpensesSummary: "/contractor/expenses/summary/",
      contractorExpensesApproveReject:
        "/contractor/expenses/:id/approve-reject/",
      contractorContractExpensesPendingApproval:
        "/contractor/contracts/:id/expenses/review/",
      contractorContractExpensesApproved:
        "/contractor/contracts/:id/expenses/approved/",
      contractorExpensesMarkInvoiced:
        "/contractor/expenses/:id/mark-as-invoiced/",
      contractorExpenseReceiptUploadUrl:
        "/contractor/expenses/receipt/upload-url/",
      contractorExpenseReceiptDownloadUrl:
        "/contractor/expenses/receipt/download-url/",
      contractorContractExpensesSummary:
        "/contractor/contracts/:id/expenses/summary/",

      // Payroll Validator endpoints
      createValidationSession: "/api/payroll-validator/sessions/create/",
      getValidationSession: "/api/payroll-validator/sessions/:sessionId/",
      getValidationSessionStatus: "/api/payroll-validator/sessions/:sessionId/status/",
      getValidationSessionSummary: "/api/payroll-validator/sessions/:sessionId/summary/",
      getValidationUploadUrl: "/api/payroll-validator/upload-url/",
      registerValidationDocument: "/api/payroll-validator/documents/register/",
      startValidation: "/api/payroll-validator/sessions/:sessionId/validate/",
      getValidationIssues: "/api/payroll-validator/sessions/:sessionId/issues/",
      getValidationIssue: "/api/payroll-validator/sessions/:sessionId/issues/:issueId/",
      getSessionEmployees: "/api/payroll-validator/sessions/:sessionId/employees/",
      getEmployeesWithIssues: "/api/payroll-validator/sessions/:sessionId/employees-with-issues/",
      // Demo endpoints (stateless)
      validateBikDemo: "/api/payroll-validator/demo/validate-bik/",
      parseFpsDemo: "/api/payroll-validator/demo/parse-fps/",
      uploadEPSData: "/api/payroll-validator/sessions/:sessionId/eps/upload/",
      getEPSReconciliation: "/api/payroll-validator/sessions/:sessionId/eps/reconciliation/",
      getStatutoryReports: "/api/payroll-validator/sessions/:sessionId/statutory-reports/",
      listSessionExports: "/api/payroll-validator/sessions/:sessionId/exports/",
      downloadEarningsExport: "/api/payroll-validator/sessions/:sessionId/exports/earnings/",
      downloadDeductionsExport: "/api/payroll-validator/sessions/:sessionId/exports/deductions/",
      downloadPayRegisterExport: "/api/payroll-validator/sessions/:sessionId/exports/pay-register/",
      downloadPensionsExport: "/api/payroll-validator/sessions/:sessionId/exports/pensions/",

      // Pension setup endpoints
      savePensionBankAccount: "/employees/benefits/pension-setup/bank-account/",
      recordPensionConsent: "/employees/benefits/pension-setup/consent/",
      validatePensionSetup: "/employees/benefits/pension-setup/validate/",
      validatePensionPostponement:
        "/employees/benefits/pension-setup/validate-postponement/",
      getPensionBankAccount:
        "/employees/benefits/pension-setup/bank-account/get/",
      getPensionConsent: "/employees/benefits/pension-setup/consent/get/",
      getPensionTermsPdf: "/employees/benefits/pension-setup/terms-pdf/",

      // Pension Quick Actions
      pensionSetup: "/employees/benefits/pensions/:employeeId/setup/",
      pensionPostpone: "/employees/benefits/pensions/:employeeId/postpone/",
      pensionCatchUp: "/employees/benefits/pensions/:employeeId/catch-up/",
      pensionChangeEmployerContribution:
        "/employees/benefits/pensions/:employeeId/employer-contribution/",
      pensionEmployeeEnroll:
        "/employees/benefits/pensions/:employeeId/employee/enroll/",
      pensionEmployeeOptOut:
        "/employees/benefits/pensions/:employeeId/employee/opt-out/",
      pensionEmployeeReenroll:
        "/employees/benefits/pensions/:employeeId/employee/re-enroll/",
      pensionEmployeeChangeContribution:
        "/employees/benefits/pensions/:employeeId/employee/contribution/",
      pensionStatus: "/employees/benefits/pensions/:employeeId/status/",
      pensionPostponementStatus:
        "/employees/benefits/pensions/:employeeId/postponement-status/",

      // Two-Factor Authentication endpoints
      getTwoFactorStatus: "/users/2fa/status/",
      initiateTwoFactorSetup: "/users/2fa/setup/initiate/",
      completeTwoFactorSetup: "/users/2fa/setup/complete/",
      verifyTwoFactorCode: "/users/2fa/verify/",
      disableTwoFactor: "/users/2fa/disable/",
      regenerateBackupCodes: "/users/2fa/backup-codes/regenerate/",
      getTwoFactorAuditLog: "/users/2fa/audit-log/",

      // Task Status Monitor endpoints
      taskStatusSummary: "/operations/api/task-status/",
      taskHistory: "/operations/api/task-status/:task_key/history/",
      // Demo Accounts endpoint
      getDemoAccounts: "/operations/api/demo-accounts/",

      // Multi-Country Platform endpoints
      getEnabledCountries: "/contractor/countries/",
      getCountryConfig: "/contractor/country-config/:countryCode/",
      usContractorOnboard: "/contractor/us/onboard/",
      getCountryProfile: "/contractor/profile/country/",
      updateCountryProfile: "/contractor/profile/country/",

      // Stripe OAuth endpoints
      stripeOAuthConnect: "/contractor/stripe/oauth/connect/",
      stripeOAuthCallback: "/contractor/stripe/oauth/callback/",
      stripeDisconnect: "/contractor/stripe/oauth/disconnect/",
      stripeStatus: "/contractor/stripe/oauth/status/",

      // Payment link endpoints for clients
      invoicePaymentLink:
        "/contractor/client/invoices/:invoiceId/payment-link/",

      // EPS endpoints
      generateEpsNoPayment: "/employees/api/filings/eps/generate-no-payment/",
      previewEps: "/employees/api/filings/eps/preview/",
      getEpsPeriods: "/employees/api/filings/eps/periods/",
      getEpsFilings: "/employees/api/filings/eps/filings/",
      validateEpsRequest: "/employees/api/filings/eps/validate/",

      // Customer AI endpoints
      customerAIChat: "/api/customer-ai/chat/",
      customerAIMultiAgentChat: "/api/customer-ai/multi-agent-chat/",
      customerAIHistory: "/api/customer-ai/history/:session_id/",
      customerAISessions: "/api/customer-ai/sessions/",
    },
  },
};

export const getApiConfig = (): ApiConfig => {
  // You can also use other environment variables here
  const environment = process.env.REACT_APP_ENV || "development";
  return config[environment as keyof EnvironmentConfig];
};

// Utility function to get full endpoint URL
export const getEndpointUrl = (
  endpointKey: string,
  params?: Record<string, string | number>,
): string => {
  const { baseURL, endpoints } = getApiConfig();
  let endpoint = endpoints[endpointKey];

  if (!endpoint) {
    throw new Error(`Endpoint ${endpointKey} not found in configuration`);
  }

  // Replace URL parameters
  if (params) {
    Object.entries(params).forEach(([key, value]) => {
      endpoint = endpoint.replace(`:${key}`, value.toString());
    });
  }

  return `${baseURL}${endpoint}`;
};

// Type-safe way to reference endpoints
export const EndpointKeys = {
  LOGIN: "login",
  ENHANCED_LOGIN: "enhancedLogin",
  COMPLETE_2FA_LOGIN: "complete2faLogin",
  VERIFY_SESSION_2FA: "verifySession2fa",
  SIGNUP: "signup",
  PTO_POLICY: "ptoPolicy",
  UPDATE_PTO_POLICY: "updatePtoPolicy",
  VALIDATE_PTO_POLICY: "validatePtoPolicy",
  FETCH_EMPLOYEE_DEDUCTIONS: "fetchEmployeeDeductions",
  FETCH_EMPLOYEE_EARNINGS: "fetchEmployeeEarnings",
  FETCH_AVAILABLE_RATE_TYPES: "fetchAvailableRateTypes",
  FETCH_EMPLOYER_FILINGS: "fetchEmployerFilings",
  FETCH_ALL_USERS: "fetchAllUsers",
  FETCH_EMPLOYER_FILING_DOCUMENT: "fetchEmployerFilingDocument",
  REFRESH_EMPLOYER_FILING: "refreshEmployerFiling",
  SUBMIT_FILING_MANUALLY: "submitFilingManually",
  SUBMIT_FILING_TIL: "submitFilingTil",
  GET_TRANSMISSION_STATUS: "getTransmissionStatus",
  GET_FILING_TRANSMISSIONS: "getFilingTransmissions",
  REMOVE_CUSTOM_RATE: "removeCustomRate",
  ADD_CUSTOM_RATE: "addCustomRate",
  TIME_OFF: "timeOff",
  FETCH_IMPORTS: "fetchImports",
  CREATE_P60_IMPORTS: "createP60Import",
  UPDATE_P60_IMPORTS: "updateP60Import",
  HANDLE_IMPORT_PROVIDER_TOGGLE: "handleImportProviderToggle",
  FETCH_PAYRUN_EMPLOYEES: "fetchPayrunEmployees",
  FETCH_PAYRUN_EARNINGS: "fetchPayrunEarnings",
  FETCH_PAYRUN_DEDUCTIONS: "fetchPayrunDeductions",
  CREATE_EARNING_OVERRIDE: "createEarningOverride",
  REMOVE_EARNING_OVERRIDE: "removeEarningOverride",
  CREATE_DEDUCTION_OVERRIDE: "createDeductionOverride",
  REMOVE_DEDUCTION_OVERRIDE: "removeDeductionOverride",
  REFRESH_PAYRUN: "refreshPayrun",
  FETCH_PTO_SETTINGS: "fetchPtoSettings",
  UPDATE_PTO_SETTINGS: "updatePtoSettings",
  GET_LEAVE_TYPES: "getLeaveTypes",
  FETCH_LEAVE_POLICIES: "fetchLeavePolicies",
  FETCH_LEAVES: "fetchLeaves",
  FETCH_PENSIONS_OVERVIEW: "fetchPensionsOverview",
  FETCH_ENROLLMENT_DETAILS: "fetchEnrollmentDetails",
  CREATE_ENROLLMENT: "createEnrollment",
  SUBMIT_SELECTIONS: "submitSelections",
  GET_PAYRUNS: "getPayruns",
  CREATE_PAYRUN: "createPayrun",
  GET_PAYRUN_EMPLOYEES: "getPayrunEmployees",
  APPROVE_PAYRUN: "approvePayrun",
  UNAPPROVE_PAYRUN: "unapprovePayrun",
  DOWNLOAD_PAYSTUB: "downloadPaystub",
  GET_EMPLOYEES: "getEmployees",
  CREATE_EMPLOYEE: "createEmployee",
  UPDATE_EMPLOYEE: "updateEmployee",
  GET_EMPLOYEE_DETAILS: "getEmployeeDetails",
  FETCH_EMPLOYER: "fetchEmployer",
  CREATE_EMPLOYER: "createEmployer",
  UPDATE_DEMOGRAPHIC_INFO: "updateDemographicInfo",
  CREATE_DEMOGRAPHIC_INFO: "createDemographicInfo",
  GET_JOB_TIMELINE: "getJobTimeline",
  UPDATE_JOB_TIMELINE: "updateJobTimeline",
  // Job Quick Actions constants
  GET_JOB_QUICK_ACTIONS_TIMELINE: "getJobQuickActionsTimeline",
  GET_JOB_TIMELINE_SUMMARY: "getJobTimelineSummary",
  VALIDATE_SETUP_STEP: "validateSetupStep",
  COMMIT_SETUP_WIZARD: "commitSetupWizard",
  // Post-hire actions (Phase 3)
  CREATE_PAY_CHANGE: "createPayChange",
  CREATE_HOURS_CHANGE: "createHoursChange",
  GET_NEXT_PAYDAY_INFO: "getNextPaydayInfo",
  GET_BASIS_GUARDRAILS: "getBasisGuardrails",
  GET_EMPLOYEE_START_DATE_INFO: "getEmployeeStartDateInfo",
  // Phase 4 actions
  CREATE_BASIS_SWITCH: "createBasisSwitch",
  CREATE_EMPLOYMENT_TERMINATION: "createEmploymentTermination",
  LOG_TIME_OFF: "logTimeOff",
  UPDATE_TAX_INFO: "updateTaxInfo",
  GET_PARENT_INFO: "getParentInfo",
  CREATE_UPDATE_PARENT_INFO: "createUpdateParentInfo",
  VALIDATE_PARENT_INFO: "validateParentInfo",
  CHECK_PAYRUN_READINESS: "checkPayrunReadiness",
  // Nanny submission endpoints
  GET_SUBMISSION: "getSubmission",
  SUBMIT_SUBMISSION: "submitSubmission",
  REOPEN_SUBMISSION: "reopenSubmission",
  REOPEN_SUBMISSION_ADMIN: "reopenSubmissionAdmin",
  // Hours worked endpoints
  CREATE_HOURS: "createHours",
  DELETE_HOURS: "deleteHours",
  // Expenses endpoints
  CREATE_EXPENSE: "createExpense",
  DELETE_EXPENSE: "deleteExpense",
  GET_EXPENSE_UPLOAD_URL: "getExpenseUploadUrl",
  GET_RECEIPT_DOWNLOAD_URL: "getReceiptDownloadUrl",
  // Time off request endpoints
  CREATE_TIME_OFF: "createTimeOff",
  DELETE_TIME_OFF: "deleteTimeOff",
  GET_NANNY_INVITES: "getNannyInvites",
  SEND_NANNY_INVITE: "sendNannyInvites",
  VALIDATE_NANNY_INVITE: "validateNannyInvite",
  REGISTER_NANNY: "registerNanny",
  GET_EMPLOYEE_SUBMISSIONS: "getEmployeeSubmissions",
  GET_EMPLOYER_CHECKLIST: "getEmployerChecklist",
  GET_ONBOARDING_INCOMPLETE_COUNT: "getOnboardingIncompleteCount",

  // Operations Dashboard Endpoints
  GET_EMPLOYER_LIST: "getEmployerList",
  GET_EMPLOYER_DETAIL: "getEmployerDetail",
  GET_EMPLOYER_ISSUES: "getEmployerIssues",
  GET_ACTIVE_ISSUES: "getActiveIssues",
  GET_ISSUES_BY_CATEGORY: "getIssuesByCategory",
  GET_ISSUE_DETAIL: "getIssueDetail",
  OVERRIDE_ISSUE: "overrideIssue",
  GET_AFFILIATE_PARTNERS: "getAffiliatePartners",
  VALIDATE_RESET_TOKEN: "validateResetToken",
  CONFIRM_PASSWORD_RESET: "confirmPasswordReset",
  GET_AFFILIATE_STATS: "getAffiliateStats",
  GET_DASHBOARD_VIEWS: "getDashboardView",
  GET_EMPLOYEE_PAYSLIPS: "getEmployeePayslips",
  GENERATE_AGREEMENT: "generateAgreement",
  DOWNLOAD_AGREEMENT: "downloadAgreement",
  GET_AGREEMENT_DOWNLOAD_URL: "getAgreementDownloadUrl",
  LIST_EMPLOYEE_AGREEMENTS: "listEmployeeAgreements",
  LIST_EMPLOYER_AGREEMENTS: "listEmployerAgreements",
  REQUEST_PASSWORD_RESET: "requestPasswordReset",
  UPDATE_SUBSCRIPTION_IMPLEMENTATION_STATUS:
    "updateSubscriptionImplementationStatus",
  APPROVE_SUBMISSION: "approveSubmission",
  REJECT_SUBMISSION: "rejectSubmission",
  GET_ADMIN_SCRIPTS: "get_admin_scripts",
  SAVE_ADMIN_SCRIPT: "save_admin_script",
  EXECUTE_ADMIN_CODE: "executeAdminCode",
  GET_USER_DETAILS: "getUserDetails",
  GET_BILLING_PLANS: "getBillingPlans",
  VALIDATE_DISCOUNT_CODE: "validateDiscountCode",
  CREATE_SUBSCRIPTION: "createSubscription",
  CANCEL_SUBSCRIPTION: "cancelSubscription",
  GET_SUBSCRIPTION: "getSubscription",
  CREATE_SETUP_INTENT: "createSetupIntent",
  UPDATE_PAYMENT_METHOD: "updatePaymentMethod",
  CREATE_PAYMENT_INTENT: "createPaymentIntent",
  ATTACH_PAYMENT_METHOD: "attachPaymentMethod",
  // Seat subscription endpoints
  GET_SEAT_SUMMARY: "getSeatSummary",
  CREATE_SEAT_CHECKOUT: "createSeatCheckout",
  UPDATE_SEATS: "updateSeats",
  // Termination endpoints
  GET_TERMINATION: "getTermination",
  CREATE_TERMINATION: "createTermination",
  UPDATE_TERMINATION: "updateTermination",
  SUBMIT_TERMINATION: "submitTermination",
  APPROVE_TERMINATION: "approveTermination",
  LIST_PENDING_TERMINATIONS: "listPendingTerminations",
  GET_IMPLEMENTATION_EMPLOYERS: "getImplementationEmployers",
  GET_FEATURES: "getFeatures",
  TOGGLE_FEATURE: "toggleFeature",
  GET_FEATURE_FLAGS: "getFeatureFlags",
  TOGGLE_FEATURE_FLAG: "toggleFeatureFlag",
  CREATE_FEATURE_FLAG: "createFeatureFlag",
  UPDATE_FEATURE_FLAG: "updateFeatureFlag",
  GET_USER_FEATURE_FLAGS: "getUserFeatureFlags",
  CREATE_USER_FEATURE_FLAG: "createUserFeatureFlag",
  DELETE_USER_FEATURE_FLAG: "deleteUserFeatureFlag",
  GET_PUBLIC_FEATURE_FLAGS: "getPublicFeatureFlags",
  // Add working hours endpoints
  GET_EMPLOYEE_SCHEDULE: "getEmployeeSchedule",
  GET_EMPLOYEE_SCHEDULE_HISTORY: "getEmployeeScheduleHistory",
  UPDATE_EMPLOYEE_SCHEDULE: "updateEmployeeSchedule",
  GET_FINAL_PAY_COMPONENTS: "getFinalPayComponents",
  REQUEST_DEMO_ACCOUNTS: "requestDemoAccounts",
  REQUEST_CONTRACTOR_DEMO_ACCOUNTS: "requestContractorDemoAccounts",
  GET_COUNTRY_FROM_IP: "getCountryFromIp",
  // Add endpoints for editing, deleting, and getting agreement details
  GET_AGREEMENT_DETAILS: "getAgreementDetails",
  UPDATE_AGREEMENT: "updateAgreement",
  DELETE_AGREEMENT: "deleteAgreement",
  GENERATE_LITE_AGREEMENT: "generateLiteAgreement",
  // Contractor agreement endpoints
  GENERATE_CONTRACTOR_AGREEMENT: "generateContractorAgreement",
  DOWNLOAD_CONTRACTOR_AGREEMENT: "downloadContractorAgreement",
  LIST_CONTRACTOR_AGREEMENTS: "listContractorAgreements",
  // Contractor quiz endpoints
  SUBMIT_CONTRACTOR_QUIZ: "submitContractorQuiz",
  GET_CONTRACTOR_QUIZ_RESULT: "getContractorQuizResult",

  // AI Contract Builder endpoints
  CONTRACT_BUILDER_CREATE: "contractBuilderCreate",
  CONTRACT_BUILDER_GET: "contractBuilderGet",
  CONTRACT_BUILDER_AI_EDIT: "contractBuilderAIEdit",
  CONTRACT_BUILDER_SAVE: "contractBuilderSave",
  CONTRACT_BUILDER_FINALIZE: "contractBuilderFinalize",
  CONTRACT_BUILDER_MY_DRAFTS: "contractBuilderMyDrafts",
  CONTRACT_BUILDER_ACCESS_EDITOR: "contractBuilderAccessEditor",
  CONTRACT_BUILDER_UPDATE_VARIABLES: "contractBuilderUpdateVariables",

  SALARY_CALCULATOR_CALCULATION: "salaryCalculatorCalculation",
  SALARY_CALCULATOR_LANGGRAPH_CHAT: "salaryCalculatorLangGraphChat",
  OPERATIONS_CONTRACTORS: "operationsContractors",
  OPERATIONS_CREATE_CONTRACTOR: "operationsCreateContractor",
  SAVE_CONTRACTOR_ENTITY: "saveContractorEntity",
  GET_CONTRACTOR_ENTITY: "getContractorEntity",
  UPDATE_CONTRACTOR_ENTITY: "updateContractorEntity",
  // Contractor entities endpoints
  GET_CONTRACTOR_ENTITIES: "getContractorEntities",
  UPDATE_CONTRACTOR_ENTITY_STATUS: "updateContractorEntityStatus",
  // Invoice endpoints
  GET_INVOICES: "getInvoices",
  GET_AVAILABLE_CONTRACTS: "getAvailableContracts",
  CREATE_INVOICE: "createInvoice",
  UPDATE_INVOICE: "updateInvoice",
  GET_INVOICE_DETAILS: "getInvoiceDetails",
  UPDATE_INVOICE_STATUS: "updateInvoiceStatus",
  SUBMIT_INVOICE: "submitInvoice",
  DELETE_INVOICE: "deleteInvoice",
  DOWNLOAD_INVOICE_DOCUMENT: "downloadInvoiceDocument",
  // Contractor contract endpoints
  GET_CONTRACTOR_CONTRACTS: "getContractorContracts",
  CREATE_CONTRACTOR_CONTRACT: "createContractorContract",
  UPDATE_CONTRACTOR_CONTRACT: "updateContractorContract",
  // Client invite endpoints
  SEND_CLIENT_INVITE: "sendClientInvite",
  VALIDATE_CLIENT_INVITE: "validateClientInvite",
  REGISTER_CLIENT: "registerClient",
  // Client invoice endpoints
  GET_CLIENT_CONTRACTS: "getClientContracts",
  APPROVE_CLIENT_CONTRACT: "approveClientContract",
  GET_CLIENT_INVOICES: "getClientInvoices",
  GET_CLIENT_INVOICE_DETAILS: "getClientInvoiceDetails",
  APPROVE_CLIENT_INVOICE: "approveClientInvoice",
  REJECT_CLIENT_INVOICE: "rejectClientInvoice",
  GET_CLIENT_DASHBOARD_DATA: "getClientDashboardData",
  CREATE_AFFILIATE: "createAffiliate",
  // One-time payment endpoints
  CREATE_ONE_TIME_PAYMENT: "createOneTimePayment",
  CONFIRM_ONE_TIME_PAYMENT: "confirmOneTimePayment",
  GET_ENTITY_SETUP_PLAN: "getEntitySetupPlan",
  // Onboarding override endpoints
  CREATE_ONBOARDING_OVERRIDE: "createOnboardingOverride",
  REMOVE_ONBOARDING_OVERRIDE: "removeOnboardingOverride",
  GET_EMPLOYER_OVERRIDES: "getEmployerOverrides",
  GET_AVAILABLE_OVERRIDE_STEP_TYPES: "getAvailableOverrideStepTypes",

  // Contractor expense endpoints
  CONTRACTOR_EXPENSES_CREATE: "contractorExpensesCreate",
  CONTRACTOR_EXPENSES_SUBMIT: "contractorExpensesSubmit",
  CONTRACTOR_EXPENSES_LIST: "contractorExpensesList",
  CONTRACTOR_EXPENSES_DETAILS: "contractorExpensesDetails",
  CONTRACTOR_EXPENSES_SUMMARY: "contractorExpensesSummary",
  CONTRACTOR_EXPENSES_APPROVE_REJECT: "contractorExpensesApproveReject",
  CONTRACTOR_CONTRACT_EXPENSES_PENDING_APPROVAL:
    "contractorContractExpensesPendingApproval",
  CONTRACTOR_CONTRACT_EXPENSES_APPROVED: "contractorContractExpensesApproved",
  CONTRACTOR_EXPENSES_MARK_INVOICED: "contractorExpensesMarkInvoiced",
  CONTRACTOR_EXPENSE_RECEIPT_UPLOAD_URL: "contractorExpenseReceiptUploadUrl",
  CONTRACTOR_EXPENSE_RECEIPT_DOWNLOAD_URL:
    "contractorExpenseReceiptDownloadUrl",
  CONTRACTOR_CONTRACT_EXPENSES_SUMMARY: "contractorContractExpensesSummary",

  // Pension setup endpoints
  SAVE_PENSION_BANK_ACCOUNT: "savePensionBankAccount",
  RECORD_PENSION_CONSENT: "recordPensionConsent",
  VALIDATE_PENSION_SETUP: "validatePensionSetup",
  VALIDATE_PENSION_POSTPONEMENT: "validatePensionPostponement",
  GET_PENSION_BANK_ACCOUNT: "getPensionBankAccount",
  GET_PENSION_CONSENT: "getPensionConsent",
  GET_PENSION_TERMS_PDF: "getPensionTermsPdf",

  // Pension Quick Actions
  PENSION_SETUP: "pensionSetup",
  PENSION_POSTPONE: "pensionPostpone",
  PENSION_CATCH_UP: "pensionCatchUp",
  PENSION_CHANGE_EMPLOYER_CONTRIBUTION: "pensionChangeEmployerContribution",
  PENSION_EMPLOYEE_ENROLL: "pensionEmployeeEnroll",
  PENSION_EMPLOYEE_OPT_OUT: "pensionEmployeeOptOut",
  PENSION_EMPLOYEE_REENROLL: "pensionEmployeeReenroll",
  PENSION_EMPLOYEE_CHANGE_CONTRIBUTION: "pensionEmployeeChangeContribution",
  PENSION_STATUS: "pensionStatus",
  PENSION_POSTPONEMENT_STATUS: "pensionPostponementStatus",

  // Two-Factor Authentication endpoints
  GET_TWO_FACTOR_STATUS: "getTwoFactorStatus",
  INITIATE_TWO_FACTOR_SETUP: "initiateTwoFactorSetup",
  COMPLETE_TWO_FACTOR_SETUP: "completeTwoFactorSetup",
  VERIFY_TWO_FACTOR_CODE: "verifyTwoFactorCode",
  DISABLE_TWO_FACTOR: "disableTwoFactor",
  REGENERATE_BACKUP_CODES: "regenerateBackupCodes",
  GET_TWO_FACTOR_AUDIT_LOG: "getTwoFactorAuditLog",

  // Task Status Monitor endpoints
  TASK_STATUS_SUMMARY: "taskStatusSummary",
  TASK_HISTORY: "taskHistory",
  // Demo Accounts endpoint
  GET_DEMO_ACCOUNTS: "getDemoAccounts",

  // Multi-Country Platform endpoints
  GET_ENABLED_COUNTRIES: "getEnabledCountries",
  GET_COUNTRY_CONFIG: "getCountryConfig",
  US_CONTRACTOR_ONBOARD: "usContractorOnboard",
  GET_COUNTRY_PROFILE: "getCountryProfile",
  UPDATE_COUNTRY_PROFILE: "updateCountryProfile",

  // Stripe OAuth endpoints
  STRIPE_OAUTH_CONNECT: "stripeOAuthConnect",
  STRIPE_OAUTH_CALLBACK: "stripeOAuthCallback",
  STRIPE_DISCONNECT: "stripeDisconnect",
  STRIPE_STATUS: "stripeStatus",

  // Payment link endpoints for clients
  INVOICE_PAYMENT_LINK: "invoicePaymentLink",

  // EPS endpoints
  GENERATE_EPS_NO_PAYMENT: "generateEpsNoPayment",
  PREVIEW_EPS: "previewEps",
  GET_EPS_PERIODS: "getEpsPeriods",
  GET_EPS_FILINGS: "getEpsFilings",
  VALIDATE_EPS_REQUEST: "validateEpsRequest",

  // Customer AI endpoints
  CUSTOMER_AI_CHAT: "customerAIChat",
  CUSTOMER_AI_MULTI_AGENT_CHAT: "customerAIMultiAgentChat",
  CUSTOMER_AI_HISTORY: "customerAIHistory",
  CUSTOMER_AI_SESSIONS: "customerAISessions",

  // Payroll Validator endpoints
  CREATE_VALIDATION_SESSION: "createValidationSession",
  GET_VALIDATION_SESSION: "getValidationSession",
  GET_VALIDATION_SESSION_STATUS: "getValidationSessionStatus",
  GET_VALIDATION_SESSION_SUMMARY: "getValidationSessionSummary",
  GET_VALIDATION_UPLOAD_URL: "getValidationUploadUrl",
  REGISTER_VALIDATION_DOCUMENT: "registerValidationDocument",
  START_VALIDATION: "startValidation",
  GET_VALIDATION_ISSUES: "getValidationIssues",
  GET_VALIDATION_ISSUE: "getValidationIssue",
  GET_SESSION_EMPLOYEES: "getSessionEmployees",
  GET_EMPLOYEES_WITH_ISSUES: "getEmployeesWithIssues",
  UPLOAD_EPS_DATA: "uploadEPSData",
  GET_EPS_RECONCILIATION: "getEPSReconciliation",
  GET_STATUTORY_REPORTS: "getStatutoryReports",
  LIST_SESSION_EXPORTS: "listSessionExports",
  DOWNLOAD_EARNINGS_EXPORT: "downloadEarningsExport",
  DOWNLOAD_DEDUCTIONS_EXPORT: "downloadDeductionsExport",
  DOWNLOAD_PAY_REGISTER_EXPORT: "downloadPayRegisterExport",
  DOWNLOAD_PENSIONS_EXPORT: "downloadPensionsExport",
  VALIDATE_BIK_DEMO: "validateBikDemo",
  PARSE_FPS_DEMO: "parseFpsDemo",
} as const;

export type EndpointKey = (typeof EndpointKeys)[keyof typeof EndpointKeys];
